import React, { useState, useEffect } from "react";
import { useMeasure } from 'react-use';

import Group2 from "../components/Group2";
import { initialisePageSkins } from "../Users";
import { useUser } from "../components/UserContext";
import '../texteditstyle.css';
import PersonIcon from '@mui/icons-material/Person';

export default function SkinViewer({pageAuthor, pageContent, setInput}) {
  const pageSkin = {...pageContent.skin};
  const {currentUser} = useUser();
  const [groups, setGroups] = useState([]);
  const [displayedGroups, setDisplayedGroups] = useState([]);
  const [heights, setHeights] = useState([]);
  const recalcTimerRef = React.useRef(null);
  const recalcArgsRef  = React.useRef(null);
  // optional: keep a ref to the latest groups so the timeout sees fresh state
  const groupsRef = React.useRef(groups);
  React.useEffect(() => { groupsRef.current = groups; }, [groups]);
  const [titleRef, {right: titleRight}] = useMeasure();
  const [titleFontSize, setTitleFontSize] = useState(70);
  const titleFontRef = React.useRef(titleFontSize);
  useEffect(() => { titleFontRef.current = titleFontSize; }, [titleFontSize]);
  const [maxTitleWidth, setMaxTitleWidth] = useState();
  const [heightChecked, setHeightChecked] = useState(false);

  useEffect(() => { //initialise
    const initializegroupStartingHeights = () => {
      let initializedHeights = [];
      for (let i = 0; i < 1; i++) {
        initializedHeights.push(40)
      }
      setHeights(initializedHeights);
    };
    setGroups([...pageContent.groups]);

    initialisePageSkins(pageContent.skin);
    initializegroupStartingHeights();
  }, [currentUser.id, pageAuthor, pageContent.skin, pageContent.groups]);

  useEffect(() => {
    setDisplayedGroups(groups);
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
    .skin-viewer-group:hover .border-block, .skin-viewer-group:hover {
    border: 1px solid ${pageSkin.borderColor}
    }`
    document.head.appendChild(styleElement);
  }, [groups, pageSkin.borderColor]);

  useEffect(() => { //check/adjust titleFontSize
    if (titleRight > 0 && !heightChecked) {
      const maxPageTitleWidth = window.innerWidth > 600 ? window.innerWidth * 0.9 - 160 : window.innerWidth * 0.9 - 80;
      setMaxTitleWidth(maxPageTitleWidth);
      if (titleRight > maxPageTitleWidth) {
        const newFontSize = Math.round((maxPageTitleWidth / titleRight) * 70);
        setTitleFontSize(newFontSize);
      }
      setHeightChecked(true);
    }
  }, [titleRight, titleFontSize, heightChecked]);

  function updateGroupHeight(order, newHeight) {
    setHeights(prevHeights => {
      const updatedHeights = [...prevHeights];
      updatedHeights[order] = newHeight;
      return updatedHeights;
    });
  }

  function updateBlockWidth( groupOrder, blockId, blockOrder, newColumnStart, newColumnEnd) {
    setInput(prevInput => ({
            ...prevInput,
            columnStart: newColumnStart,
            columnEnd: newColumnEnd
        }));
      setGroups(prevGroups => {
      const updatedGroups = [...prevGroups];
      updatedGroups[groupOrder].blocks[blockOrder] = {
        ...updatedGroups[groupOrder].blocks[blockOrder],
        columnStart: newColumnStart,
        columnEnd: newColumnEnd
      }
      return updatedGroups;
    })
  }

  function cleanOrder(things) {
    // Deep copy the array and the objects inside it
    return things.map((blockItem, index) => ({
      ...blockItem, // Spread the existing properties of the block
      order: index  // Update the order
    }));
  }

  function moveBlock(groupId, groupOrder, blockId, blockOrder, desiredOrder, newColumnStart, newColumnEnd) {
    setInput(prevInput => ({
            ...prevInput,
            columnStart: newColumnStart,
            columnEnd: newColumnEnd
        }));
    return new Promise((resolve) => {
  
      setGroups(prevGroups => {
        const updatedGroups = [...prevGroups];
        let movingBlock = updatedGroups[groupOrder].blocks[blockOrder];
        movingBlock.columnStart = newColumnStart;
        movingBlock.columnEnd = newColumnEnd;
  
        updatedGroups[groupOrder].blocks = updatedGroups[groupOrder].blocks.filter((_, index) => index !== blockOrder);
        updatedGroups[groupOrder].blocks.splice(desiredOrder, 0, movingBlock);
  
        updatedGroups[groupOrder].blocks = cleanOrder(updatedGroups[groupOrder].blocks);
  
        return updatedGroups;
      });
  
      // Resolve the promise once the state update is done
      resolve();
    });
  }

  function recalcBlockOrder(groupId, groupOrder, originalSH) {
    // cancel any pending run
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);

    // save the latest args
    recalcArgsRef.current = [groupOrder, originalSH];

    // schedule the heavy work for after 100ms of silence
    recalcTimerRef.current = setTimeout(() => {
      const [gOrder, oSH] = recalcArgsRef.current;

      // ===== YOUR ORIGINAL FUNCTION CONTENT (use oSH/gId/gOrder) =====
      const SHOnly = [];
      const CSOnly = [];
      const SHMostlyInOrder = [];
      const placementsPrettyMuch = [];
      const finalPlacements = []; 

      for (let i = 0; i < oSH.length; i++) {
        SHOnly.push(oSH[i].SH);
        CSOnly.push(oSH[i].CS);
      }
      const SHDec = [...SHOnly];
      const processedIndexes = new Array(SHOnly.length).fill(false); 
      for (let i = 0; i < oSH.length; i++) {
        const newLSH = Math.min(...SHDec);  
        const iOfNLSH = SHOnly.findIndex((sh, idx) => sh === newLSH && !processedIndexes[idx]); 
        SHMostlyInOrder.push(newLSH);  
        placementsPrettyMuch.push(iOfNLSH); 
        const fakeIndex = SHDec.indexOf(newLSH); 
        SHDec.splice(fakeIndex, 1); 
        processedIndexes[iOfNLSH] = true;  
      }
      for (let i = 0; i < oSH.length; i++) {
        const dupeIndexes = [];
        SHMostlyInOrder.forEach((SH, index) => {
          if (SH === SHMostlyInOrder[i]) dupeIndexes.push(index);
        });
        if (dupeIndexes.length > 1) {
          const dupePlacementIndexes = [];
          dupeIndexes.forEach((index) => {
            // const originalIndex = placementsPrettyMuch[index];
            dupePlacementIndexes.push(index); 
          });
          const sortedDupeIndexes = dupePlacementIndexes.sort((a, b) => {
            const originalIndexA = placementsPrettyMuch[a];  
            const originalIndexB = placementsPrettyMuch[b]; 
            return CSOnly[originalIndexA] - CSOnly[originalIndexB];  
          });
          sortedDupeIndexes.forEach((sortedIndex) => {
            finalPlacements.push(placementsPrettyMuch[sortedIndex]);
          });
          i += dupeIndexes.length - 1;
        } else {
          finalPlacements.push(placementsPrettyMuch[i]);
        }
      }

      const needsReorder = finalPlacements.some((place, index) => place !== index);

      if (needsReorder) {
        const updatedSH = [...oSH];
        const reorderedSH = [];
        finalPlacements.forEach((placement, i) => {
          reorderedSH[i] = updatedSH[placement];
        });

        // you had this before; keeping as-is
        finalPlacements.pop();

        // use latest groups via ref to avoid stale closures
        const currentGroups = groupsRef.current;
        const group = currentGroups?.[gOrder];
        if (!group) return;

        // update UI
        setTimeout(() => {
          setGroups(prevGroups => {
            const updatedGroups = [...prevGroups];
            const groupToUpdate = updatedGroups[gOrder];
            if (!groupToUpdate) return prevGroups;

            const movingBlocks = [...groupToUpdate.blocks];
            let reorderedBlocks = [];
            finalPlacements.forEach((placement, i) => {
              reorderedBlocks[i] = movingBlocks[placement];
            });
            reorderedBlocks = cleanOrder(reorderedBlocks);

            updatedGroups[gOrder] = { ...groupToUpdate, blocks: reorderedBlocks };
            return updatedGroups;
          });
        }, 0);
      }
      // ===== END original content =====
    }, 30);

    // immediate, lightweight return for every call pre-debounce
    return originalSH;
  }

  return (
    <div>
      {pageSkin.backgroundImage && <img id="page-background" src={pageSkin.backgroundImage} alt="background"/>}
      <div className="page-head" id="skin-viewer-page-head">
        <div className="page-title" style={{top:"300px"}}>
          {pageAuthor.pfp? <img className="actual-creator-pfp" src={`${pageAuthor.pfp}`} alt="mock pfp" /> :
          <div className="actual-creator-pfp"><PersonIcon className="creator-default-pfp"/></div>}
          <h2 className="title-text" ref={titleRef} style={{fontSize: `${titleFontSize}px`, ...(heightChecked ? {maxWidth: `${maxTitleWidth}px`, overflow: "hidden"} : {})}}>{pageContent.title}</h2>
          <h3>by <span className="a-sub">{pageAuthor?.username}</span></h3>
        </div>
      </div>

      <div id="block-area" style={{left:`${(88 - pageSkin.groupWidth) / 2}vw`}}>
        {displayedGroups.map((groupItem, index) => (
          <Group2
            key={groupItem.id}
            groupId={groupItem.id}
            groupOrder={groupItem.order}
            title={groupItem.title}
            blocks={groupItem.blocks}
            timeCreated={groupItem.timeCreated}
            link={groupItem.link}
            height={heights[groupItem.order]}
            onHeightChange={updateGroupHeight}
            onWidthChange={updateBlockWidth}
            recalcOrder={recalcBlockOrder}
            moveBlock={moveBlock}
            isEditing={true}
            isProfile={false}
            isSkinViewer={true}
            pageAuthor={pageAuthor.id}
            currentUser={currentUser.id}
            pageSkin={pageSkin}
          />
        ))}
      </div>
      <div id="space"></div>
    </div>
  );
}